import { createSelector, createSlice } from '@reduxjs/toolkit';
import { isAfter, isSameDay, parse } from 'date-fns';
import * as R from 'ramda';

// Utils
import http from 'utils/http';
import { FILTER_ALL } from 'utils/constants';
import { createSnack } from 'snackbar/snackbarSlice';

const initialState = {
  error: '',
  fetchPtoListPending: true,
  filterDrawerOpen: false,
  filters: {
    affiliate: FILTER_ALL,
    dataCompany: FILTER_ALL,
    location: FILTER_ALL,
    originator: FILTER_ALL,
    recipient: FILTER_ALL,
    transferDate: FILTER_ALL,
  },
  openPtoList: [],
};

const reducers = {
  fetchOpenPTOsSuccess(state, { payload }) {
    return R.assoc('openPtoList', payload, state);
  },
  resetFilters(state) {
    const initState = {
      affiliate: FILTER_ALL,
      dataCompany: FILTER_ALL,
      location: FILTER_ALL,
      originator: FILTER_ALL,
      recipient: FILTER_ALL,
      transferDate: FILTER_ALL,
    };
    return R.assoc('filters', initState, state);
  },
  resetSingleFilter(state, { payload }) {
    return R.assocPath(['filters', payload], FILTER_ALL, state);
  },
  setFetchPtoListPending(state, { payload }) {
    return R.assoc('fetchPtoListPending', payload, state);
  },
  setFilterDrawerOpen(state, { payload }) {
    return R.assoc('filterDrawerOpen', payload, state);
  },
  setFilters(state, { payload: { name, value } }) {
    return R.assocPath(['filters', name], value, state);
  },
};

const { actions, reducer } = createSlice({
  initialState,
  name: 'openPtos',
  reducers,
});

// Selectors
const ptoDateInFuture = (ptoItem, value) =>
  value === 'Future Only' &&
  isAfter(parse(ptoItem.transferDate, 'M/d/yyyy', new Date()), new Date());

const ptoDateIsToday = (ptoItem, value) =>
  value === 'Today Only' &&
  isSameDay(parse(ptoItem.transferDate, 'M/d/yyyy', new Date()), new Date());

const originatorOrRecipient = (ptoItem, value) =>
  value === ptoItem.recipient || value === ptoItem.originator;

// Not the best way to do this but much easier here in reducer
// than done in Open.jsx and passed down the tree
export const getFilteredPtoList = createSelector(
  state => state.pto.openPtos.openPtoList,
  state => state.pto.openPtos.filters,
  (openPtoList, filters) => {
    // Filter the list of ptos if filters are applied
    const filteredList = openPtoList.filter(item =>
      Object.keys(filters).some(key => {
        switch (key) {
          // Filtered by affiliate needs to check against
          // originator or recipient and return that
          case 'affiliate':
            return originatorOrRecipient(item, filters.affiliate);
          case 'transferDate':
            return (
              ptoDateInFuture(item, filters.transferDate) ||
              ptoDateIsToday(item, filters.transferDate)
            );
          default:
            return filters[key] === item[key];
        }
      })
    );
    // Since filters and PTO is in need of clean up and refactor
    // leave this check here, it essentially says if we have applied any filter
    // to return the "filtered" PTO list, if not return the full pto list
    const filterApplied = Object.keys(filters).some(key => filters[key] !== FILTER_ALL);
    return filterApplied ? filteredList : openPtoList;
  }
);

const getDropdownOptions = (array, optionalEl) => {
  const mappedArray = array.map(el => ({
    label: el,
    value: el,
  }));
  return optionalEl ? [{ label: optionalEl, value: optionalEl }, ...mappedArray] : mappedArray;
};

export const getFilterOptions = createSelector(
  state => state.pto.openPtos.openPtoList,
  openPtoList => ({
    affiliateListDropdown: openPtoList.length
      ? getDropdownOptions(
          R.uniq(
            openPtoList.map(
              ({ affiliateIsRecipient, affiliateIsOriginator, recipient, originator }) => {
                if (affiliateIsRecipient) return recipient;
                if (affiliateIsOriginator) return originator;
                return null;
              }
            )
          ),
          FILTER_ALL
        )
      : [],
    dataCompanyOptions: openPtoList.length
      ? getDropdownOptions(R.uniq(openPtoList.map(pto => pto.dataCompany)), FILTER_ALL)
      : [],
    locations: openPtoList.length
      ? getDropdownOptions([...new Set(openPtoList.map(pto => pto.location))], FILTER_ALL)
      : [],
    originatorOptions: openPtoList.length
      ? getDropdownOptions(R.uniq(openPtoList.map(pto => pto.originator)), FILTER_ALL)
      : [],
    recipientOptions: openPtoList.length
      ? getDropdownOptions(R.uniq(openPtoList.map(pto => pto.recipient)), FILTER_ALL)
      : [],
    transferDates: ['All', 'Today Only', 'Future Only'].map(transferDate => ({
      label: transferDate,
      value: transferDate,
    })),
  })
);

export const getFilterIdentifierCount = createSelector(
  state => state.pto.openPtos.filters,
  filters => Object.keys(filters).filter(key => filters[key] !== FILTER_ALL).length
);

export const getNumberFuturePtos = createSelector(
  state => state.pto.openPtos.openPtoList,
  openPtoList =>
    openPtoList.filter(ptoItem =>
      isAfter(parse(ptoItem.transferDate, 'M/d/yyyy', new Date()), new Date())
    ).length
);

// Action Creators
export const {
  fetchOpenPTOsSuccess,
  resetFilters,
  resetSingleFilter,
  setFetchPtoListPending,
  setFilters,
  setFilterDrawerOpen,
} = actions;

let fetchPtoController;
export const fetchOpenPTOs = () => (dispatch, getState) => {
  dispatch(setFetchPtoListPending(true));

  if (fetchPtoController) fetchPtoController.abort();

  fetchPtoController = new AbortController();

  const { signal } = fetchPtoController;

  const url = `/product-transfer-orders/view-open`;
  http
    .get(url, { signal })
    .then(({ data }) => {
      if (!R.equals(data, getState().pto.openPtos.openPtoList)) {
        dispatch(fetchOpenPTOsSuccess(data));
      }
    })
    .catch(err =>
      createSnack({
        message: `Error fetching open ptos: ${err}`,
        options: { variant: 'error' },
      })
    )
    .finally(() => dispatch(setFetchPtoListPending(false)));
};

export default reducer;
